/* Lesson content model. A lesson is a list of blocks; text fields use the inline formatter:
   **bold**, {{pc:C}} (note name), {{n:C4}} (note with octave), {{c:Am}} (chord that plays on click).
   Note names are shown in the user's naming system (Do-Re-Mi / C-D-E). */
import type { PlaySpec } from '../../core/audio';

export type { PlaySpec };

/** A play button: spec fields plus an optional label. */
export type LabeledSpec = PlaySpec & { label?: string };

export type TableCell = string | number | { label?: string; play: PlaySpec } | LabeledSpec;

export type Block =
  | { t: 'p'; text: string }
  | { t: 'h'; text: string }
  | { t: 'list'; items: string[]; ordered?: boolean }
  | { t: 'tip'; tone?: 'info' | 'warn' | 'success'; title?: string; text: string }
  | { t: 'steps'; items: { title: string; text: string }[] }
  | ({ t: 'play'; label?: string } & PlaySpec)
  | { t: 'compare'; items: LabeledSpec[] }
  | { t: 'table'; head?: TableCell[]; rows: TableCell[][] }
  | {
      t: 'fretboard';
      /** notes with octave, e.g. "E2 E3 E4" */
      notes?: string;
      /** pitch classes, e.g. "E G A B D" */
      pcs?: string;
      root?: string;
      from?: number;
      to?: number;
      caption?: string;
    }
  | { t: 'keyboard'; from?: string; to?: string; highlight?: string; caption?: string }
  /** one string drawn to scale, nut to bridge (real fret spacing); frets play that string */
  | { t: 'neck'; frets?: number; string?: number; caption?: string }
  | { t: 'chords'; names: string; caption?: string }
  | {
      t: 'quiz';
      q: string;
      play?: PlaySpec;
      items?: LabeledSpec[];
      options: string[];
      /** index into options */
      answer: number;
      explain?: string;
      hint?: string;
    }
  | { t: 'breath'; pattern?: [number, number, number]; rounds?: number }
  | { t: 'go'; label: string; route: string };

export type BlockType = Block['t'];

export interface Lesson {
  /** unique within the module, e.g. "g1" */
  id: string;
  title: string;
  minutes?: number;
  summary?: string;
  blocks: Block[];
}

// ---- builders keep the lesson files compact; the result is plain data ----
export const p = (text: string): Block => ({ t: 'p', text });
export const h = (text: string): Block => ({ t: 'h', text });
export const list = (items: string[], ordered = false): Block => ({ t: 'list', items, ordered });
export const tip = (tone: 'info' | 'warn' | 'success', title: string, text: string): Block => ({
  t: 'tip',
  tone,
  title,
  text,
});
export const steps = (items: [string, string][]): Block => ({
  t: 'steps',
  items: items.map(([title, text]) => ({ title, text })),
});
export const play = (label: string, spec: PlaySpec): Block => ({ t: 'play', label, ...spec });
export const compare = (items: [string, PlaySpec][]): Block => ({
  t: 'compare',
  items: items.map(([label, spec]) => ({ label, ...spec })),
});
export const table = (head: TableCell[], rows: TableCell[][]): Block => ({ t: 'table', head, rows });
/** A play button in a table cell; without a label it shows only the play icon and is named after its row. */
export const cell = (spec: PlaySpec, label = ''): TableCell => ({ label, play: spec });
export const go = (label: string, route: string): Block => ({ t: 'go', label, route });
/** Piano voice for vocal lessons. */
export const pn = (spec: PlaySpec): PlaySpec => ({ inst: 'piano', ...spec });
